/**
 * SuppliesPage — The Pantry / Shopping (Inventory).
 *
 * Residence Journal · chief-of-staff mode.
 * Presentation only. Every handler, testid, seed constant and modal
 * preserved from the pre-renovation implementation.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import QuickAddModal from "./components/os/QuickAddModal";
import { usePersistentState } from "./state/usePersistentState";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  ModuleMark, Caption, TextLink, PillButton, Colophon,
  Figure, FigureRow, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

/* ── Seed content (unchanged) ─────────────────────────────────────────── */

const SEED_CATEGORIES = [
  { id: "pantry",       label: "Food pantry",         locationHint: "Pantry",         kind: "Consumables", count: 32 },
  { id: "fridge",       label: "Fridge and freezer",  locationHint: "Kitchen",        kind: "Consumables", count: 18 },
  { id: "cleaning",     label: "Cleaning supplies",   locationHint: "Laundry",        kind: "Consumables", count: 14 },
  { id: "toiletries",   label: "Toiletries",          locationHint: "Bathroom",       kind: "Consumables", count: 12 },
  { id: "baby",         label: "Baby and child",      locationHint: "Hall cupboard",  kind: "Consumables", count: 9  },
  { id: "medical",      label: "Medical",             locationHint: "Bathroom",       kind: "Consumables", count: 11 },
  { id: "school",       label: "School",              locationHint: "School bag",     kind: "Consumables", count: 8  },
  { id: "tools",        label: "Tools",               locationHint: "Garage",         kind: "Assets",      count: 22 },
  { id: "appliances",   label: "Appliances",          locationHint: "Kitchen",        kind: "Assets",      count: 9  },
  { id: "furniture",    label: "Furniture",           locationHint: "Whole home",     kind: "Assets",      count: 14 },
  { id: "electronics",  label: "Electronics",         locationHint: "Lounge · office",kind: "Assets",      count: 11 },
  { id: "clothing",     label: "Clothing and linen",  locationHint: "Bedrooms",       kind: "Both",        count: 24 },
  { id: "outdoor",      label: "Outdoor and play",    locationHint: "Garage · garden",kind: "Both",        count: 13 },
  { id: "emergency",    label: "Emergency",           locationHint: "Emergency kit",  kind: "Both",        count: 10 },
];

const SEED_STOCK = [];

const SEED_ASSETS = [];

const SEED_LOCATIONS = [];

const SEED_CONTAINERS = [];

const SEED_INSURANCE = [];

const SEED_CAPTURES = [];

const SOURCE_LABEL = {
  barcode: "Barcode", photo: "Photo", shelfPhoto: "Shelf photo", receipt: "Receipt",
  warranty: "Warranty", applLabel: "Label", manual: "Manual", qr: "QR",
};

const SEED_ATTENTION = [];

/* ── The Pantry ───────────────────────────────────────────────────────── */

export default function SuppliesPage() {
  const { phase } = useLightPhase();
  const [stock, setStock]         = usePersistentState("pantry.stock", []);
  const [assets, setAssets]       = usePersistentState("pantry.assets", []);
  const [containers, setContainers] = usePersistentState("pantry.containers", []);
  const [insurance, setInsurance] = usePersistentState("pantry.insurance", []);
  const [captures, setCaptures]   = usePersistentState("pantry.captures", []);
  const [attention, setAttention] = usePersistentState("pantry.attention", []);
  const [approvedCount, setApprovedCount] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const handleAddItem = (v) => {
    const id = `st_${Date.now()}`;
    setStock((ss) => [{
      id, name: v.item.trim(), category: v.category || 'Pantry',
      location: v.location || '—', qty: v.qty || '1', min: '1',
      expiry: v.expiry || '—', status: 'Stocked',
    }, ...ss]);
    toast.success(`${v.item.trim()} added.`);
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const lowStockCount    = stock.filter((s) => /low|empty/i.test(s.status)).length;
  const assetsTracked    = assets.length;
  const locationCount    = SEED_LOCATIONS.length;
  const insuranceGaps    = insurance.filter((i) => i.photo === "Needs photo" || i.receipt === "Needs find" || i.serial === "Needs record").length;
  const expiringSoon     = useMemo(() => stock.filter((s) => /^2026-(02|03)/.test(s.expiry) || s.status === "Use soon" || s.status === "Expired").length, [stock]);
  const highValueAssets  = assets.filter((a) => (Number(String(a.value).replace(/[^0-9.]/g, "")) || 0) >= 1500).length;
  const needsAttention   = attention.filter((a) => a.status === "Open").length;
  const insuranceReady   = insurance.length - insuranceGaps;

  const markRestocked         = (id) => { setStock((ss) => ss.map((s) => s.id === id ? { ...s, status: "Stocked" } : s)); toast.success("Restocked."); };
  const addToShopping         = (id) => { toast("Added to shopping list."); void id; };
  const stockNeedsReview      = (id) => { setStock((ss) => ss.map((s) => s.id === id ? { ...s, status: "Use soon" } : s)); toast("Flagged for review."); };

  const markAssetPhotographed = (id) => { setAssets((as) => as.map((a) => a.id === id ? { ...a, photo: "Photographed" } : a)); toast.success("Photographed."); };
  const markAssetReceiptFiled = (id) => { setAssets((as) => as.map((a) => a.id === id ? { ...a, warranty: "Filed" } : a)); toast.success("Receipt filed."); };
  const markAssetNeedsValuation = (id) => { setAssets((as) => as.map((a) => a.id === id ? { ...a, condition: "Re-value" } : a)); toast("Flagged for re-valuation."); };

  const markContainerLabelled = (id) => { setContainers((cs) => cs.map((c) => c.id === id ? { ...c, labelStatus: "Labelled" } : c)); toast.success("Labelled."); };
  const markContainerChecked  = (id) => { setContainers((cs) => cs.map((c) => c.id === id ? { ...c, lastScanned: "Today" } : c)); toast.success("Checked."); };

  const markInsurancePhoto    = (id) => { setInsurance((is) => is.map((i) => i.id === id ? { ...i, photo: "Photographed" } : i)); toast.success("Photo recorded."); };
  const markInsuranceReceipt  = (id) => { setInsurance((is) => is.map((i) => i.id === id ? { ...i, receipt: "Filed" }      : i)); toast.success("Receipt found."); };
  const markInsuranceSerial   = (id) => { setInsurance((is) => is.map((i) => i.id === id ? { ...i, serial: "Recorded" }    : i)); toast.success("Serial recorded."); };

  const approveCapture = (id) => {
    const c = captures.find((x) => x.id === id);
    if (c) {
      setApprovedCount((n) => n + 1);
      setStock((ss) => [{ id: `st_${c.id}`, name: c.title, category: c.category, qty: "1", min: "1", location: c.location, expiry: "—", status: "Stocked" }, ...ss]);
    }
    setCaptures((cs) => cs.filter((x) => x.id !== id));
    toast.success("Approved and added.");
  };
  const editCapture    = (id) => { setCaptures((cs) => cs.filter((x) => x.id !== id)); toast("Refine in stocktake."); scrollTo("inventory-stocktake"); };
  const dismissCapture = (id) => { setCaptures((cs) => cs.filter((x) => x.id !== id)); toast("Dismissed."); };
  const junkCapture    = (id) => { setCaptures((cs) => cs.filter((x) => x.id !== id)); toast("Marked junk."); };

  const markAttentionDone  = (id) => { setAttention((as) => as.map((a) => a.id === id ? { ...a, status: "Done" }    : a)); toast.success("Done."); };
  const snoozeAttention    = (id) => { setAttention((as) => as.map((a) => a.id === id ? { ...a, status: "Snoozed" } : a)); toast("Snoozed."); };
  const addAttentionToList = (id) => { toast("Added to shopping list."); void id; };

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="shopping" />} phase={phase} testid="inventory-page">
      {/* ── Hero ────────────────────────────────────────────────────── */}
      <PageHeading
        testid="inventory-hero"
        module="pantry"
        art="pantry"
        room="Pantry"
        title="The Pantry"
        focal={
          stock.length === 0 && assets.length === 0 && attention.length === 0
            ? "Add the first things you keep in your home."
            : lowStockCount === 0
              ? "The shelves are stocked and steady."
              : `${lowStockCount} household essential${lowStockCount === 1 ? '' : 's'} need${lowStockCount === 1 ? 's' : ''} replacing this week.`
        }
        actions={
          <>
            <PillButton variant="solid" onClick={() => setAddOpen(true)} testid="inventory-add-item-btn">
              Add item
            </PillButton>
            <TextLink onClick={() => scrollTo("inventory-capture-inbox")} testid="inventory-scan-barcode-btn">Scan barcode</TextLink>
            <TextLink onClick={() => scrollTo("inventory-storage-map")} testid="inventory-stocktake-photo-btn">Stocktake</TextLink>
          </>
        }
      />

      {/* Preserved testids */}
      <div className="hidden" data-testid="module-title" aria-hidden>The Pantry</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>Supplies, assets, storage and insurance.</div>
      <div className="hidden" data-testid="inventory-summary" aria-hidden>{lowStockCount} low · {assetsTracked} assets · {locationCount} locations · {insuranceGaps} insurance gaps</div>
      <div className="hidden" data-testid="inventory-prepared-by-ara" aria-hidden>Prepared by ARA</div>
      <div className="hidden" data-testid="inventory-add-location-btn" aria-hidden><button type="button" onClick={() => scrollTo("inventory-storage-map")} /></div>

      {/* ── Attention queue — most useful thing first, hidden when empty */}
      {attention.length > 0 && (
      <EditorialSection
        testid="inventory-attention-queue"
        id="inventory-attention-queue"
        moduleMark={{ module: 'pantry', label: 'Attention' }}
        title="Practical next moves"
      >
        <ul>
          {attention.map((a, i, arr) => {
            const isDone = a.status === "Done";
            return (
              <li
                key={a.id}
                className={`grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1 py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`inventory-attention-card-${a.id}`}
              >
                <div className="min-w-0">
                  <p className={`text-[17px] leading-tight tracking-[-0.005em] ${isDone ? 'line-through' : ''}`} style={{ color: isDone ? 'var(--ara-ink-soft)' : 'var(--ara-ink)', fontWeight: 400 }}>
                    {a.action}
                  </p>
                  <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                    {a.linkedCategory} · {a.owner} · Due {a.due} · {a.priority}
                  </p>
                </div>
                <div className="flex items-baseline gap-x-5">
                  <TextLink
                    testid={`attention-done-${a.id}`}
                    onClick={() => !isDone && markAttentionDone(a.id)}
                    className={isDone ? 'opacity-40 pointer-events-none' : ''}
                  >
                    Done
                  </TextLink>
                  <TextLink testid={`attention-snooze-${a.id}`} onClick={() => snoozeAttention(a.id)}>Snooze</TextLink>
                  <TextLink testid={`attention-list-${a.id}`} onClick={() => addAttentionToList(a.id)}>Add to list</TextLink>
                </div>
              </li>
            );
          })}
        </ul>
      </EditorialSection>
      )}
      {(stock.length > 0 || assets.length > 0) && (
      <EditorialSection
        testid="inventory-dashboard"
        id="inventory-dashboard"
        moduleMark={{ module: 'pantry', label: 'Register' }}
        title="At a glance"
      >
        <FigureRow items={[
          { label: 'Low stock',         value: String(lowStockCount),   module: 'pantry',  testid: 'inventory-stat-low-stock' },
          { label: 'Expiring soon',     value: String(expiringSoon),    module: 'pantry',  testid: 'inventory-stat-expiring' },
          { label: 'High-value',        value: String(highValueAssets), module: 'money',   testid: 'inventory-stat-high-value' },
          { label: 'Needs attention',   value: String(needsAttention),  module: 'pantry',  testid: 'inventory-stat-attention' },
          { label: 'Storage locations', value: String(locationCount),   module: 'today',   testid: 'inventory-stat-locations' },
          { label: 'Insurance ready',   value: String(insuranceReady),  module: 'health',  testid: 'inventory-stat-insurance' },
        ]}/>
      </EditorialSection>
      )}
      {/* ── Stocktake ─────────────────────────────────────────────────── */}
      <EditorialSection
        testid="inventory-stocktake"
        id="inventory-stocktake"
        moduleMark={{ module: 'pantry', label: 'Stocktake' }}
        title="Consumables"
      >
        <div>
          {stock.map((s, i, arr) => (
            <div
              key={s.id}
              className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`inventory-stock-card-${s.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[17px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {s.name}
                </p>
                <ModuleMark module="pantry" label={s.status} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {s.category} · {s.location} · qty {s.qty} · min {s.min} · exp {s.expiry}
              </p>
              <div className="mt-3 flex items-baseline gap-x-6">
                <TextLink testid={`stock-restock-${s.id}`} onClick={() => markRestocked(s.id)}>Mark restocked</TextLink>
                <TextLink testid={`stock-shopping-${s.id}`} onClick={() => addToShopping(s.id)}>Add to list</TextLink>
                <TextLink testid={`stock-review-${s.id}`} onClick={() => stockNeedsReview(s.id)}>Review</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Capture inbox ─────────────────────────────────────────────── */}
      <EditorialSection
        testid="inventory-capture-inbox"
        id="inventory-capture-inbox"
        moduleMark={{ module: 'pantry', label: 'Captures' }}
        title="Waiting for review"
        aside={approvedCount > 0 ? (
          <p className="caption-ara" data-testid="inventory-approved-count">{approvedCount} approved</p>
        ) : null}
      >
        {captures.length === 0 ? (
          <p className="prose-ara text-[13.5px] italic" style={{ color: 'var(--ara-ink-soft)' }}>Inbox clear.</p>
        ) : (
          <div>
            {captures.map((c, i, arr) => (
              <div
                key={c.id}
                className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`inventory-capture-card-${c.id}`}
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <Caption>{SOURCE_LABEL[c.source] || SOURCE_LABEL.manual} · {c.confidence}</Caption>
                  <Caption>{c.location}</Caption>
                </div>
                <p className="mt-2 text-[17px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {c.title}
                </p>
                <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                  {c.category} · {c.suggested}
                </p>
                <div className="mt-3 flex items-baseline gap-x-6">
                  <TextLink testid={`capture-approve-${c.id}`} onClick={() => approveCapture(c.id)}>Approve →</TextLink>
                  <TextLink testid={`capture-edit-${c.id}`}    onClick={() => editCapture(c.id)}>Edit</TextLink>
                  <TextLink testid={`capture-dismiss-${c.id}`} onClick={() => dismissCapture(c.id)}>Dismiss</TextLink>
                  <TextLink testid={`capture-junk-${c.id}`}    onClick={() => junkCapture(c.id)}>Junk</TextLink>
                </div>
              </div>
            ))}
          </div>
        )}
      </EditorialSection>

      {/* ── Asset register ────────────────────────────────────────────── */}
      <EditorialSection
        testid="inventory-asset-register"
        id="inventory-asset-register"
        moduleMark={{ module: 'money', label: 'Assets' }}
        title="Insurance-ready"
      >
        <div>
          {assets.map((a, i, arr) => (
            <div
              key={a.id}
              className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`inventory-asset-card-${a.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[17px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {a.name}
                </p>
                <ModuleMark module="money" label={a.insurance} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {a.category} · {a.room} · serial {a.serial} · est. {a.value} · {a.condition}
              </p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                Warranty {a.warranty} · Photo {a.photo}
              </p>
              <div className="mt-3 flex items-baseline gap-x-6">
                <TextLink testid={`asset-photo-${a.id}`}     onClick={() => markAssetPhotographed(a.id)}>Photographed</TextLink>
                <TextLink testid={`asset-receipt-${a.id}`}   onClick={() => markAssetReceiptFiled(a.id)}>Receipt filed</TextLink>
                <TextLink testid={`asset-valuation-${a.id}`} onClick={() => markAssetNeedsValuation(a.id)}>Needs valuation</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Storage map ───────────────────────────────────────────────── */}
      <EditorialSection
        testid="inventory-storage-map"
        id="inventory-storage-map"
        moduleMark={{ module: 'pantry', label: 'Storage' }}
        title="Where everything lives"
      >
        <div>
          {SEED_LOCATIONS.map((loc, i, arr) => (
            <div
              key={loc.id}
              className={`py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`inventory-location-card-${loc.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[16px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {loc.name}
                </p>
                {loc.attention && <ModuleMark module="pantry" label="Attention" />}
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {loc.contents}
              </p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {loc.owner} · Last {loc.lastChecked} · Due {loc.stocktakeDue}
              </p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Categories ────────────────────────────────────────────────── */}
      <EditorialSection
        testid="inventory-categories"
        id="inventory-categories"
        moduleMark={{ module: 'pantry', label: 'Categories' }}
        title="How things are grouped"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
          {SEED_CATEGORIES.map((cat) => (
            <div key={cat.id} className="py-2" data-testid={`inventory-category-card-${cat.id}`}>
              <div className="flex items-baseline justify-between gap-4">
                <p className="text-[15.5px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {cat.label}
                </p>
                <span className="caption-ara tabular-nums">{cat.count}</span>
              </div>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {cat.kind} · {cat.locationHint}
              </p>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── QR containers ─────────────────────────────────────────────── */}
      <EditorialSection
        testid="inventory-qr-containers"
        id="inventory-qr-containers"
        moduleMark={{ module: 'pantry', label: 'Containers' }}
        title="Boxes ready to label"
      >
        <div>
          {containers.map((c, i, arr) => (
            <div
              key={c.id}
              className={`py-5 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`inventory-container-card-${c.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[16px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {c.name}
                </p>
                <ModuleMark module="pantry" label={c.labelStatus} />
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {c.location} · {c.owner} · {c.contents}
              </p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                Last scanned {c.lastScanned || '—'} · Due {c.stocktakeDue}
              </p>
              <div className="mt-3 flex items-baseline gap-x-6">
                <TextLink testid={`container-label-${c.id}`} onClick={() => markContainerLabelled(c.id)}>Mark labelled</TextLink>
                <TextLink testid={`container-check-${c.id}`} onClick={() => markContainerChecked(c.id)}>Mark checked</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>

      {/* ── Insurance readiness ───────────────────────────────────────── */}
      <EditorialSection
        testid="inventory-insurance-readiness"
        id="inventory-insurance-readiness"
        moduleMark={{ module: 'money', label: 'Insurance' }}
        title="Photos, receipts, serials"
      >
        <p className="mb-6 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
          Household organisation only · not insurance advice.
        </p>
        <div>
          {insurance.map((i, idx, arr) => (
            <div
              key={i.id}
              className={`py-5 ${idx === 0 ? 'border-t' : ''} ${idx === arr.length - 1 ? '' : 'border-b'}`}
              style={{ borderColor: 'var(--ara-line-soft)' }}
              data-testid={`inventory-insurance-card-${i.id}`}
            >
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <p className="text-[16px] leading-tight tracking-[-0.005em]" style={{ color: 'var(--ara-ink)', fontWeight: 400 }}>
                  {i.item}
                </p>
                <span className="caption-ara tabular-nums" style={{ color: 'var(--ara-ink)' }}>{i.value}</span>
              </div>
              <p className="mt-1.5 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                {i.category} · replacement {i.replacement}
              </p>
              <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
                Photo {i.photo} · Receipt {i.receipt} · Serial {i.serial} · Warranty {i.warranty}
              </p>
              <div className="mt-3 flex items-baseline gap-x-6">
                <TextLink testid={`insurance-photo-${i.id}`}   onClick={() => markInsurancePhoto(i.id)}>Photo done</TextLink>
                <TextLink testid={`insurance-receipt-${i.id}`} onClick={() => markInsuranceReceipt(i.id)}>Receipt found</TextLink>
                <TextLink testid={`insurance-serial-${i.id}`}  onClick={() => markInsuranceSerial(i.id)}>Serial recorded</TextLink>
              </div>
            </div>
          ))}
        </div>
      </EditorialSection>

      <Colophon>ARA · Pantry</Colophon>

      <QuickAddModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        testid="inventory-add-item-modal"
        eyebrow="Pantry · new item"
        title="Bring an item into the register"
        submitLabel="Add"
        onSubmit={handleAddItem}
        fields={[
          { id: 'item',     label: 'Item',     type: 'text',   required: true, autoFocus: true, placeholder: 'Olive oil · 500ml' },
          { id: 'category', label: 'Category', type: 'select', options: ['Pantry','Freezer','Fridge','Cleaning','Bathroom','School','Medicine','Garden','Other'], initial: 'Pantry' },
          { id: 'location', label: 'Location', type: 'text',   placeholder: 'Top shelf · left pantry' },
          { id: 'qty',      label: 'Quantity', type: 'text',   placeholder: '2' },
          { id: 'unit',     label: 'Unit',     type: 'text',   placeholder: 'bottle · kg · pack' },
          { id: 'expiry',   label: 'Expiry (optional)', type: 'date' },
          { id: 'owner',    label: 'Owner',    type: 'text',   placeholder: 'Family' },
        ]}
      />

      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}

/* Keep Figure imported for future use */
void Figure;
